import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { useToast } from '../../../shared/components/Toast';
import { useCriarBaseComercial } from '../hooks';
import { adicionarItem, adicionarServico } from '../api';
import {
  BaseComercialForm,
  BaseComercialFormState,
  buildCreatePayload,
} from '../components/BaseComercialForm';

/**
 * Página `/base-comercial/nova` — criação de Base Comercial (task 10.3).
 *
 * Fluxo em duas etapas: (1) `POST /commercial-bases` cria a Base em
 * Em Elaboração com os campos de identificação/precificação/reajuste;
 * (2) para cada serviço/perfil cadastrado, chama os endpoints de
 * estrutura (`POST .../services` + `POST .../items`). Se a estrutura
 * falhar a meio, a Base já existe em Em Elaboração — o usuário continua
 * o preenchimento no detalhe.
 */
export default function NovaBaseComercialPage(): React.ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  const criar = useCriarBaseComercial();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = async (state: BaseComercialFormState) => {
    setSubmitError(null);
    try {
      const payload = buildCreatePayload(state);
      const base = await criar.mutateAsync(payload);

      // Estrutura: cria serviços e itens na Base recém-criada.
      for (const sv of state.servicos) {
        const servico = await adicionarServico(base.id, {
          serviceTypeSlug: sv.serviceTypeSlug,
          descricao: sv.descricao.trim() || undefined,
        });
        for (const it of sv.itens) {
          await adicionarItem(base.id, {
            commercialBaseServiceId: servico.id,
            professionalProfileSlug: it.professionalProfileSlug,
            pricingModelSlug: it.pricingModelSlug,
            quantidade: Number(it.quantidade) || 1,
            valorUnitario: Number(it.valorUnitario) || 0,
            custoUnitario: it.custoUnitario !== '' ? Number(it.custoUnitario) : null,
            vigenciaInicio: it.vigenciaInicio || null,
            vigenciaFim: it.vigenciaFim || null,
            observacoes: it.observacoes.trim() || undefined,
          });
        }
      }

      toast.success(`Base ${base.codigo} criada.`);
      navigate(`/base-comercial/${base.id}`);
    } catch (err) {
      setSubmitError(getFriendlyErrorMessage(err, 'Falha ao cadastrar Base Comercial.'));
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <button
        onClick={() => navigate('/base-comercial')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Nova Base Comercial</h1>
        <p className="mt-1 text-sm text-gray-600">
          Condições comerciais negociadas com um cliente ativo (RN-COM-001). A Base nasce
          em Em Elaboração, versão 1.
        </p>
      </div>

      <BaseComercialForm
        mode="create"
        onSubmit={submit}
        onCancel={() => navigate('/base-comercial')}
        submitLabel="Criar Base Comercial"
        isSubmitting={criar.isPending}
        submitError={submitError}
      />
    </div>
  );
}
